import React, { useState } from "react";
import styled from "styled-components";
import {
    Card,
    Table,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    Select,
    TreeSelect,
    Switch,
    message,
    Space,
    Tag,
    Popconfirm,
    Typography,
    Progress,
    Statistic,
    Row,
    Col,
    Result,
    Divider,
    ColorPicker,
    DatePicker,
} from "antd";
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ThunderboltOutlined,
    UserOutlined,
    GiftOutlined,
    StarOutlined,
    CheckCircleOutlined,
    PhoneOutlined,
    DollarOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import {
    getLoyaltyCards,
    createLoyaltyCard,
    updateLoyaltyCard as updateLoyaltyCardApi,
    deleteLoyaltyCard as deleteLoyaltyCardApi,
    smartStamp,
    getLoyaltyClients,
    getLoyaltyDashboard,
    getProductCategories,
} from "../../service";
import { PillTabs } from "../../styles/shared-components";
import { colors, spacing, shadows } from "../../styles/theme";

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// ============================================
// STYLED COMPONENTS
// ============================================

const LoyaltyWrapper = styled.div`
  padding: ${spacing.xxl} 40px;
  background: ${colors.white};
  min-height: calc(100vh - 64px);

  @media (max-width: 768px) {
    padding: ${spacing.md};
  }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${colors.textPrimary};
  margin: 0 0 ${spacing.xl} 0;
`;

const TabContentFullWidth = styled.div`
  width: 100%;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled(Card)`
  border-radius: 12px !important;
  box-shadow: ${shadows.card} !important;
  border: 1px solid ${colors.border} !important;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: ${shadows.cardHover} !important;
    transform: translateY(-2px);
  }
`;

const SmartStampCard = styled(Card)`
  border-radius: 16px !important;
  box-shadow: ${shadows.medium} !important;
  border: 1px solid ${colors.border} !important;
  max-width: 500px;
  margin: 0 auto;

  .ant-card-head {
    border-bottom: none !important;
    padding-bottom: 0 !important;
  }
`;

const StampResultCard = styled(Card) <{ $success?: boolean }>`
  border-radius: 12px !important;
  margin-bottom: 12px;
  border: 1px solid ${(p) =>
        p.$success ? colors.success : colors.border} !important;
  background: ${(p) => (p.$success ? colors.successBackground : "inherit")} !important;
`;

const LoyaltyCardPreview = styled.div<{ $color?: string }>`
  background: ${(p) => p.$color || "#e11d48"};
  border-radius: 12px;
  padding: 16px;
  color: white;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 60px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);

  .card-name {
    font-size: 16px;
    font-weight: 600;
  }

  .card-stamps {
    font-size: 13px;
    opacity: 0.9;
  }
`;

// ============================================
// DASHBOARD TAB
// ============================================

const DashboardTab: React.FC = () => {
    const { t } = useTranslation();
    const { data, isLoading } = useQuery("loyalty-dashboard", getLoyaltyDashboard);

    return (
        <div>
            <DashboardGrid>
                <StatCard>
                    <Statistic
                        title={t("loyaltyPage.activeCards")}
                        value={data?.activeCards ?? 0}
                        prefix={<StarOutlined style={{ color: "#e11d48" }} />}
                    />
                </StatCard>
                <StatCard>
                    <Statistic
                        title={t("loyaltyPage.clientsWithCards")}
                        value={data?.totalClients ?? 0}
                        prefix={<UserOutlined style={{ color: "#6366f1" }} />}
                    />
                </StatCard>
                <StatCard>
                    <Statistic
                        title={t("loyaltyPage.completedCards")}
                        value={data?.completions ?? 0}
                        prefix={<CheckCircleOutlined style={{ color: "#10b981" }} />}
                    />
                </StatCard>
                <StatCard>
                    <Statistic
                        title={t("loyaltyPage.rewardsIssued")}
                        value={data?.rewards ?? 0}
                        prefix={<GiftOutlined style={{ color: "#f59e0b" }} />}
                    />
                </StatCard>
            </DashboardGrid>

            {(!data?.activeCards || data.activeCards === 0) && !isLoading && (
                <Result
                    icon={<StarOutlined style={{ color: "#e11d48" }} />}
                    title={t("loyaltyPage.noActiveCardsTitle")}
                    subTitle={t("loyaltyPage.noActiveCardsSubtitle")}
                />
            )}
        </div>
    );
};

// ============================================
// CARDS TAB — CRUD
// ============================================

const CardsTab: React.FC = () => {
    const { t } = useTranslation();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingCard, setEditingCard] = useState<any>(null);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const { data: loyaltyCards, isLoading } = useQuery(
        "loyalty-cards",
        getLoyaltyCards
    );

    // Fetch product categories with products for TreeSelect
    const { data: categories } = useQuery("product-categories-for-loyalty", () =>
        getProductCategories()
    );

    // Transform categories into TreeSelect data structure
    const treeData = React.useMemo(() => {
        if (!categories) return [];

        return categories.map((category: any) => ({
            title: category.name,
            value: `category-${category.id}`,
            key: `category-${category.id}`,
            selectable: false, // Categories are not selectable, only products
            children: (category.productBases || []).map((product: any) => ({
                title: product.name,
                value: product.id,
                key: `product-${product.id}`,
            })),
        }));
    }, [categories]);

    const createMutation = useMutation(createLoyaltyCard, {
        onSuccess: () => {
            message.success(t("loyaltyPage.cardCreated"));
            queryClient.invalidateQueries("loyalty-cards");
            queryClient.invalidateQueries("loyalty-dashboard");
            handleCloseModal();
        },
        onError: (err: any) => {
            message.error(
                t("loyaltyPage.errorPrefix", { message: err?.response?.data?.message || err.message })
            );
        },
    });

    const updateMutation = useMutation(
        ({ id, data }: { id: number; data: any }) => updateLoyaltyCardApi(id, data),
        {
            onSuccess: () => {
                message.success(t("loyaltyPage.cardUpdated"));
                queryClient.invalidateQueries("loyalty-cards");
                handleCloseModal();
            },
            onError: (err: any) => {
                message.error(
                    t("loyaltyPage.errorPrefix", { message: err?.response?.data?.message || err.message })
                );
            },
        }
    );

    const deleteMutation = useMutation(deleteLoyaltyCardApi, {
        onSuccess: () => {
            message.success(t("loyaltyPage.cardDeleted"));
            queryClient.invalidateQueries("loyalty-cards");
            queryClient.invalidateQueries("loyalty-dashboard");
        },
        onError: (err: any) => {
            message.error(
                t("loyaltyPage.errorPrefix", { message: err?.response?.data?.message || err.message })
            );
        },
    });

    const handleOpenModal = (card?: any) => {
        if (card) {
            setEditingCard(card);
            form.setFieldsValue({
                name: card.name,
                description: card.description,
                stampsRequired: card.stampsRequired,
                stampConditionType: card.stampConditionType,
                stampConditionValue: card.stampConditionValue
                    ? card.stampConditionValue / 100
                    : undefined,
                rewardType: card.rewardType,
                rewardValue: card.rewardValue,
                rewardDescription: card.rewardDescription,
                rewardValidityDays: card.rewardValidityDays,
                isActive: card.isActive,
                cardColor: card.cardColor,
                notifyOnStamp: card.notifyOnStamp,
                notifyOnComplete: card.notifyOnComplete,
                stampProductIds: card.stampProductIds || [],
                rewardProductIds: card.rewardProductIds || [],
            });
        } else {
            setEditingCard(null);
            form.resetFields();
            form.setFieldsValue({
                stampsRequired: 10,
                stampConditionType: "ORDER",
                rewardType: "COUPON_PERCENTAGE",
                rewardValidityDays: 30,
                isActive: true,
                cardColor: "#e11d48",
                notifyOnStamp: true,
                notifyOnComplete: true,
                stampProductIds: [],
                rewardProductIds: [],
            });
        }
        setIsModalVisible(true);
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
        setEditingCard(null);
        form.resetFields();
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            const cardData = {
                name: values.name,
                description: values.description,
                stampsRequired: values.stampsRequired,
                stampConditionType: values.stampConditionType,
                stampConditionValue:
                    values.stampConditionType === "AMOUNT_SPENT"
                        ? Math.round((values.stampConditionValue || 0) * 100)
                        : undefined,
                rewardType: values.rewardType,
                rewardValue: values.rewardValue,
                rewardDescription: values.rewardDescription,
                rewardValidityDays: values.rewardValidityDays,
                isActive: values.isActive ?? true,
                cardColor:
                    typeof values.cardColor === "string"
                        ? values.cardColor
                        : values.cardColor?.toHexString?.() || "#e11d48",
                notifyOnStamp: values.notifyOnStamp ?? true,
                notifyOnComplete: values.notifyOnComplete ?? true,
                stampProductIds: values.stampConditionType === "SPECIFIC_PRODUCT" ? values.stampProductIds : [],
                rewardProductIds: (values.rewardType === "FREE_PRODUCT" || values.rewardProductIds?.length > 0) ? values.rewardProductIds : [],
            };

            if (editingCard) {
                updateMutation.mutate({ id: editingCard.id, data: cardData });
            } else {
                createMutation.mutate(cardData);
            }
        } catch (err) {
            console.error("Validation failed:", err);
        }
    };

    const conditionType = Form.useWatch("stampConditionType", form);
    const rewardType = Form.useWatch("rewardType", form);

    const columns = [
        {
            title: t("loyaltyPage.columnCard"),
            key: "card",
            render: (_: any, record: any) => (
                <LoyaltyCardPreview $color={record.cardColor}>
                    <div>
                        <div className="card-name">{record.name}</div>
                        <div className="card-stamps">
                            {t("loyaltyPage.stampsCount", { count: record.stampsRequired })}
                        </div>
                    </div>
                    <StarOutlined style={{ fontSize: 24, opacity: 0.8 }} />
                </LoyaltyCardPreview>
            ),
        },
        {
            title: t("loyaltyPage.columnCondition"),
            key: "condition",
            render: (_: any, r: any) => {
                const labels: Record<string, string> = {
                    ORDER: t("loyaltyPage.conditionOrder"),
                    AMOUNT_SPENT: t("loyaltyPage.conditionAmountSpent", { value: ((r.stampConditionValue || 0) / 100).toFixed(2) }),
                    SPECIFIC_PRODUCT: t("loyaltyPage.conditionSpecificProduct"),
                };
                return <Text>{labels[r.stampConditionType] || r.stampConditionType}</Text>;
            },
        },
        {
            title: t("loyaltyPage.columnReward"),
            key: "reward",
            render: (_: any, r: any) => {
                const labels: Record<string, string> = {
                    COUPON_PERCENTAGE: t("loyaltyPage.rewardCouponPercentageValue", { value: r.rewardValue }),
                    COUPON_FIXED: t("loyaltyPage.rewardCouponFixedValue", { value: ((r.rewardValue || 0) / 100).toFixed(2) }),
                    FREE_PRODUCT: t("loyaltyPage.rewardFreeProduct"),
                };
                return (
                    <Tag color="green" icon={<GiftOutlined />}>
                        {labels[r.rewardType] || r.rewardType}
                    </Tag>
                );
            },
        },
        {
            title: t("loyaltyPage.columnStatus"),
            dataIndex: "isActive",
            key: "isActive",
            render: (v: boolean) => (
                <Tag color={v ? "success" : "default"}>
                    {v ? t("loyaltyPage.statusActive") : t("loyaltyPage.statusInactive")}
                </Tag>
            ),
        },
        {
            title: t("loyaltyPage.columnActions"),
            key: "actions",
            render: (_: any, record: any) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleOpenModal(record)}
                    >
                        {t("loyaltyPage.edit")}
                    </Button>
                    <Popconfirm
                        title={t("loyaltyPage.deleteConfirmTitle")}
                        description={t("loyaltyPage.deleteConfirmDescription")}
                        onConfirm={() => deleteMutation.mutate(record.id)}
                        okText={t("loyaltyPage.yes")}
                        cancelText={t("loyaltyPage.no")}
                    >
                        <Button type="text" danger icon={<DeleteOutlined />}>
                            {t("loyaltyPage.delete")}
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginBottom: 24,
                }}
            >
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleOpenModal()}
                >
                    {t("loyaltyPage.newCardButton")}
                </Button>
            </div>

            <Table
                dataSource={loyaltyCards || []}
                columns={columns}
                loading={isLoading}
                rowKey="id"
                pagination={{ pageSize: 10 }}
            />

            <Modal
                title={
                    editingCard
                        ? t("loyaltyPage.editCardTitle")
                        : t("loyaltyPage.newCardTitle")
                }
                open={isModalVisible}
                onCancel={handleCloseModal}
                onOk={handleSubmit}
                okText={editingCard ? t("loyaltyPage.save") : t("loyaltyPage.create")}
                cancelText={t("loyaltyPage.cancel")}
                width={640}
                confirmLoading={
                    createMutation.isLoading || updateMutation.isLoading
                }
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="name"
                        label={t("loyaltyPage.cardNameLabel")}
                        rules={[{ required: true, message: t("loyaltyPage.cardNameRequired") }]}
                    >
                        <Input placeholder={t("loyaltyPage.cardNamePlaceholder")} />
                    </Form.Item>

                    <Form.Item name="description" label={t("loyaltyPage.descriptionLabel")}>
                        <TextArea rows={2} placeholder={t("loyaltyPage.descriptionPlaceholder")} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="stampsRequired"
                                label={t("loyaltyPage.stampsRequiredLabel")}
                                rules={[{ required: true, message: t("loyaltyPage.stampsRequiredRequired") }]}
                            >
                                <InputNumber min={2} max={50} style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="stampConditionType"
                                label={t("loyaltyPage.stampConditionLabel")}
                                rules={[{ required: true }]}
                            >
                                <Select>
                                    <Option value="ORDER">{t("loyaltyPage.conditionOrder")}</Option>
                                    <Option value="AMOUNT_SPENT">
                                        {t("loyaltyPage.conditionMinAmount")}
                                    </Option>
                                    <Option value="SPECIFIC_PRODUCT">
                                        {t("loyaltyPage.conditionSpecificProduct")}
                                    </Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {conditionType === "AMOUNT_SPENT" && (
                        <Form.Item
                            name="stampConditionValue"
                            label={t("loyaltyPage.minAmountLabel")}
                            rules={[{ required: true, message: t("loyaltyPage.amountRequired") }]}
                        >
                            <InputNumber
                                min={0.01}
                                step={0.01}
                                style={{ width: "100%" }}
                                placeholder={t("loyaltyPage.amountPlaceholder")}
                            />
                        </Form.Item>
                    )}

                    {conditionType === "SPECIFIC_PRODUCT" && (
                        <Form.Item
                            name="stampProductIds"
                            label={t("loyaltyPage.selectStampProductsLabel")}
                            rules={[{ required: true, message: t("loyaltyPage.selectAtLeastOneProduct") }]}
                        >
                            <TreeSelect
                                treeData={treeData}
                                placeholder={t("loyaltyPage.selectProductsPlaceholder")}
                                multiple
                                showSearch
                                treeCheckable
                                showCheckedStrategy={TreeSelect.SHOW_CHILD}
                                style={{ width: '100%' }}
                                treeDefaultExpandAll
                                filterTreeNode={(input, node) =>
                                    (node.title as string)
                                        ?.toLowerCase()
                                        ?.indexOf(input.toLowerCase()) >= 0
                                }
                            />
                        </Form.Item>
                    )}

                    <Divider>{t("loyaltyPage.rewardDivider")}</Divider>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="rewardType"
                                label={t("loyaltyPage.rewardTypeLabel")}
                                rules={[{ required: true }]}
                            >
                                <Select>
                                    <Option value="COUPON_PERCENTAGE">{t("loyaltyPage.rewardCouponPercentage")}</Option>
                                    <Option value="COUPON_FIXED">{t("loyaltyPage.rewardCouponFixed")}</Option>
                                    <Option value="FREE_PRODUCT">{t("loyaltyPage.rewardFreeProduct")}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            {(rewardType === "COUPON_PERCENTAGE" ||
                                rewardType === "COUPON_FIXED") && (
                                    <Form.Item
                                        name="rewardValue"
                                        label={
                                            rewardType === "COUPON_PERCENTAGE"
                                                ? t("loyaltyPage.rewardValuePercent")
                                                : t("loyaltyPage.rewardValueGrosze")
                                        }
                                        rules={[{ required: true, message: t("loyaltyPage.valueRequired") }]}
                                    >
                                        <InputNumber
                                            min={1}
                                            max={rewardType === "COUPON_PERCENTAGE" ? 100 : undefined}
                                            style={{ width: "100%" }}
                                        />
                                    </Form.Item>
                                )}
                        </Col>
                    </Row>

                    {rewardType === "FREE_PRODUCT" && (
                        <Form.Item
                            name="rewardProductIds"
                            label={t("loyaltyPage.selectFreeProductsLabel")}
                            rules={[{ required: true, message: t("loyaltyPage.selectAtLeastOneProduct") }]}
                        >
                            <TreeSelect
                                treeData={treeData}
                                placeholder={t("loyaltyPage.selectProductsPlaceholder")}
                                multiple
                                showSearch
                                treeCheckable
                                showCheckedStrategy={TreeSelect.SHOW_CHILD}
                                style={{ width: '100%' }}
                                treeDefaultExpandAll
                                filterTreeNode={(input, node) =>
                                    (node.title as string)
                                        ?.toLowerCase()
                                        ?.indexOf(input.toLowerCase()) >= 0
                                }
                            />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="rewardDescription"
                        label={t("loyaltyPage.rewardDescriptionLabel")}
                    >
                        <Input placeholder={t("loyaltyPage.rewardDescriptionPlaceholder")} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="rewardValidityDays"
                                label={t("loyaltyPage.rewardValidityLabel")}
                            >
                                <InputNumber min={1} max={365} style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="cardColor" label={t("loyaltyPage.cardColorLabel")}>
                                <ColorPicker />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider>{t("loyaltyPage.notificationsDivider")}</Divider>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="notifyOnStamp"
                                label={t("loyaltyPage.smsOnStampLabel")}
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="notifyOnComplete"
                                label={t("loyaltyPage.smsOnCompleteLabel")}
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="isActive"
                        label={t("loyaltyPage.activeLabel")}
                        valuePropName="checked"
                    >
                        <Switch checkedChildren={t("loyaltyPage.yes")} unCheckedChildren={t("loyaltyPage.no")} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

// ============================================
// SMART STAMP TAB
// ============================================

const SmartStampTab: React.FC = () => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [clientForm] = Form.useForm();
    const [result, setResult] = useState<any>(null);
    const [showClientModal, setShowClientModal] = useState(false);
    const [pendingStampData, setPendingStampData] = useState<any>(null);
    const queryClient = useQueryClient();

    const stampMutation = useMutation(smartStamp, {
        onSuccess: (data: any) => {
            // If client not found, show registration modal
            if (data.clientNotFound) {
                setPendingStampData({
                    phoneNumber: data.phoneNumber,
                    orderValue: form.getFieldValue("orderValue"),
                });
                clientForm.resetFields();
                setShowClientModal(true);
                return;
            }

            setResult(data);
            if (data.stampsAdded > 0) {
                message.success(
                    data.stampsAdded === 1
                        ? t("loyaltyPage.stampsAddedSingular", { count: data.stampsAdded })
                        : t("loyaltyPage.stampsAddedPlural", { count: data.stampsAdded })
                );
            } else {
                message.info(t("loyaltyPage.noConditionsMet"));
            }
            queryClient.invalidateQueries("loyalty-clients");
            queryClient.invalidateQueries("loyalty-dashboard");
            form.resetFields();
        },
        onError: (err: any) => {
            message.error(
                t("loyaltyPage.errorPrefix", { message: err?.response?.data?.message || err.message })
            );
        },
    });

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            stampMutation.mutate({
                phoneNumber: values.phoneNumber,
                orderValue: Math.round(values.orderValue * 100),
            });
        } catch (err) {
            console.error("Validation:", err);
        }
    };

    const handleClientRegistration = async () => {
        try {
            const values = await clientForm.validateFields();
            // Re-send stamp with clientData for creating the new client
            stampMutation.mutate({
                phoneNumber: pendingStampData.phoneNumber,
                orderValue: Math.round(pendingStampData.orderValue * 100),
                clientData: {
                    name: values.name,
                    email: values.email || "",
                    allowedMarketing: values.allowedMarketing ?? false,
                },
            });
            setShowClientModal(false);
            setPendingStampData(null);
        } catch (err) {
            console.error("Client form validation:", err);
        }
    };

    return (
        <div style={{ maxWidth: 600, margin: "0 auto" }}>
            <SmartStampCard
                title={
                    <Space>
                        <ThunderboltOutlined
                            style={{ color: "#e11d48", fontSize: 20 }}
                        />
                        <span>{t("loyaltyPage.smartStampCardTitle")}</span>
                    </Space>
                }
            >
                <Text type="secondary" style={{ display: "block", marginBottom: 20 }}>
                    {t("loyaltyPage.smartStampDescription")}
                </Text>

                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        name="phoneNumber"
                        label={t("loyaltyPage.phoneNumberLabel")}
                        rules={[
                            { required: true, message: t("loyaltyPage.phoneNumberRequired") },
                            {
                                pattern: /^\+?\d{9,15}$/,
                                message: t("loyaltyPage.phoneNumberInvalid"),
                            },
                        ]}
                    >
                        <Input
                            prefix={<PhoneOutlined />}
                            placeholder="+48501234567"
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="orderValue"
                        label={t("loyaltyPage.orderValueLabel")}
                        rules={[
                            { required: true, message: t("loyaltyPage.amountRequired") },
                        ]}
                    >
                        <InputNumber
                            prefix={<DollarOutlined />}
                            min={0.01}
                            step={0.01}
                            style={{ width: "100%" }}
                            size="large"
                            placeholder={t("loyaltyPage.orderValuePlaceholder")}
                        />
                    </Form.Item>

                    <Button
                        type="primary"
                        htmlType="submit"
                        icon={<ThunderboltOutlined />}
                        loading={stampMutation.isLoading}
                        block
                        size="large"
                        style={{ background: "#e11d48", borderColor: "#e11d48" }}
                    >
                        {t("loyaltyPage.addStampsButton")}
                    </Button>
                </Form>
            </SmartStampCard>

            {/* Modal for new client registration */}
            <Modal
                title={t("loyaltyPage.newClientTitle")}
                open={showClientModal}
                onCancel={() => {
                    setShowClientModal(false);
                    setPendingStampData(null);
                }}
                onOk={handleClientRegistration}
                okText={t("loyaltyPage.registerAndAddStamps")}
                cancelText={t("loyaltyPage.cancel")}
                confirmLoading={stampMutation.isLoading}
            >
                <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
                    {t("loyaltyPage.clientNotFoundPrefix")} <Text strong>{pendingStampData?.phoneNumber}</Text> {t("loyaltyPage.clientNotFoundSuffix")}
                </Text>

                <Form form={clientForm} layout="vertical">
                    <Form.Item
                        name="name"
                        label={t("loyaltyPage.clientNameLabel")}
                        rules={[{ required: true, message: t("loyaltyPage.clientNameRequired") }]}
                    >
                        <Input placeholder={t("loyaltyPage.clientNamePlaceholder")} size="large" />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label={t("loyaltyPage.emailLabel")}
                        rules={[
                            {
                                type: "email",
                                message: t("loyaltyPage.emailInvalid"),
                            },
                        ]}
                    >
                        <Input placeholder={t("loyaltyPage.emailPlaceholder")} size="large" />
                    </Form.Item>

                    <Form.Item
                        name="allowedMarketing"
                        valuePropName="checked"
                        initialValue={false}
                    >
                        <Switch
                            checkedChildren={t("loyaltyPage.yes")}
                            unCheckedChildren={t("loyaltyPage.no")}
                        />{" "}
                        <Text style={{ marginLeft: 8 }}>{t("loyaltyPage.marketingConsent")}</Text>
                    </Form.Item>
                </Form>
            </Modal>

            {result && (
                <div style={{ marginTop: 24 }}>
                    <Title level={5}>
                        {result.stampsAdded === 1
                            ? t("loyaltyPage.resultTitleSingular", { count: result.stampsAdded })
                            : t("loyaltyPage.resultTitlePlural", { count: result.stampsAdded })}
                    </Title>
                    {result.results?.map((r: any, idx: number) => (
                        <StampResultCard key={idx} $success={r.stamped}>
                            <Card.Meta
                                title={
                                    <Space>
                                        {r.stamped ? (
                                            <CheckCircleOutlined
                                                style={{ color: "#10b981" }}
                                            />
                                        ) : (
                                            <span>⊘</span>
                                        )}
                                        {r.cardName}
                                    </Space>
                                }
                                description={
                                    r.stamped ? (
                                        <Space direction="vertical" size={4}>
                                            <Text>
                                                {t("loyaltyPage.progressLabel", { current: r.stampsAfter, total: r.stampsRequired })}
                                            </Text>
                                            <Progress
                                                percent={Math.round(
                                                    (r.stampsAfter / r.stampsRequired) * 100
                                                )}
                                                strokeColor="#e11d48"
                                                size="small"
                                            />
                                            {r.completed && (
                                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                                    <Tag color="gold" icon={<GiftOutlined />}>
                                                        {t("loyaltyPage.cardCompletedReward")}
                                                    </Tag>
                                                    {r.reward?.couponCode && (
                                                        <div style={{ marginTop: 4 }}>
                                                            <Text strong>{t("loyaltyPage.rewardCodeLabel")} </Text>
                                                            <Tag color="purple" style={{ fontSize: 14, padding: "4px 8px" }}>
                                                                {r.reward.couponCode}
                                                            </Tag>
                                                        </div>
                                                    )}
                                                </Space>
                                            )}
                                        </Space>
                                    ) : (
                                        <Text type="secondary">{r.reason}</Text>
                                    )
                                }
                            />
                        </StampResultCard>
                    ))}
                </div>
            )}
        </div>
    );
};

// ============================================
// CLIENTS TAB
// ============================================

const ClientsTab: React.FC = () => {
    const { t } = useTranslation();
    const { data: clients, isLoading } = useQuery(
        "loyalty-clients",
        getLoyaltyClients
    );

    const columns = [
        {
            title: t("loyaltyPage.columnClient"),
            key: "client",
            render: (_: any, r: any) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{r.name}</Text>
                    <Text type="secondary">{r.phoneNumber}</Text>
                </Space>
            ),
        },
        {
            title: t("loyaltyPage.columnCards"),
            key: "cards",
            render: (_: any, r: any) =>
                r.loyaltyCards?.map((clc: any) => (
                    <div key={clc.id} style={{ marginBottom: 8 }}>
                        <Space>
                            <div
                                style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    background: clc.loyaltyCard?.cardColor || "#e11d48",
                                    display: "inline-block",
                                }}
                            />
                            <Text>{clc.loyaltyCard?.name}</Text>
                        </Space>
                        <Progress
                            percent={Math.round(
                                (clc.currentStamps /
                                    (clc.loyaltyCard?.stampsRequired || 1)) *
                                100
                            )}
                            size="small"
                            strokeColor={clc.loyaltyCard?.cardColor || "#e11d48"}
                            format={() =>
                                `${clc.currentStamps}/${clc.loyaltyCard?.stampsRequired}`
                            }
                        />
                    </div>
                )),
        },
        {
            title: t("loyaltyPage.columnCompletions"),
            key: "completions",
            render: (_: any, r: any) => {
                const total = r.loyaltyCards?.reduce(
                    (sum: number, c: any) => sum + (c.completedCount || 0),
                    0
                );
                return total > 0 ? (
                    <Tag color="green">{total}x</Tag>
                ) : (
                    <Text type="secondary">—</Text>
                );
            },
        },
        {
            title: t("loyaltyPage.columnRewards"),
            key: "rewards",
            render: (_: any, r: any) => {
                const activeRewards = r.loyaltyCards?.flatMap((clc: any) =>
                    (clc.rewards || []).map((rw: any) => ({
                        ...rw,
                        cardName: clc.loyaltyCard?.name,
                    }))
                );

                if (!activeRewards || activeRewards.length === 0) {
                    return <Text type="secondary">—</Text>;
                }

                return (
                    <Space direction="vertical" size={4}>
                        {activeRewards.map((rw: any) => (
                            <Tag key={rw.id} color="gold" icon={<GiftOutlined />}>
                                {rw.couponCode} ({rw.cardName})
                            </Tag>
                        ))}
                    </Space>
                );
            },
        },
    ];

    return (
        <Table
            dataSource={clients || []}
            columns={columns}
            loading={isLoading}
            rowKey="id"
            pagination={{ pageSize: 20 }}
        />
    );
};

// ============================================
// LOYALTY CONTENT (embeddable, no wrapper/title)
// ============================================

export const LoyaltyContent: React.FC = () => {
    const { t } = useTranslation();
    const loyaltyItems = [
        {
            key: "loyalty-dashboard",
            label: "Dashboard",
            children: (
                <TabContentFullWidth>
                    <DashboardTab />
                </TabContentFullWidth>
            ),
        },
        {
            key: "loyalty-cards",
            label: t("loyaltyPage.tabCards"),
            children: (
                <TabContentFullWidth>
                    <CardsTab />
                </TabContentFullWidth>
            ),
        },
        {
            key: "loyalty-stamp",
            label: "Smart Stamp",
            children: (
                <TabContentFullWidth>
                    <SmartStampTab />
                </TabContentFullWidth>
            ),
        },
        {
            key: "loyalty-clients",
            label: t("loyaltyPage.tabClients"),
            children: (
                <TabContentFullWidth>
                    <ClientsTab />
                </TabContentFullWidth>
            ),
        },
    ];

    return (
        <PillTabs defaultActiveKey="loyalty-dashboard" items={loyaltyItems} />
    );
};

// ============================================
// MAIN COMPONENT (standalone page — kept for backward compat)
// ============================================

export const Loyalty: React.FC = () => {
    const { t } = useTranslation();
    return (
        <LoyaltyWrapper>
            <PageTitle>{t("loyaltyPage.pageTitle")}</PageTitle>
            <LoyaltyContent />
        </LoyaltyWrapper>
    );
};
